import { ChangeDetectorRef, Component, OnDestroy, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { ComputerService } from 'src/app/computer/computer.service';
import { ThermalPrintTargetService } from 'src/app/computer/thermal-print-target.service';
import { clearCart, removeFromCart, updateProductInCart } from 'src/app/redux/actions/cart.actions';
import { AppState } from 'src/app/redux/app.state';
import { NotificationService } from 'src/app/services/notification.service';
import { SupportService } from 'src/app/support.service';
import { Product, ProductWithControls, User } from 'src/typings';

@Component({
  selector: 'app-cart-c',
  templateUrl: './cart-c.component.html',
  styleUrls: ['./cart-c.component.scss']
})
export class CartCComponent implements OnDestroy {
  @ViewChild('assemblyMenuTrigger') assemblyMenuTrigger?: MatMenuTrigger;

  user: User | undefined;

  cart: FormArray = this.fb.array([]);

  storeSubscription: Subscription;

  selectedProductsFormArray: FormArray = this.fb.array([]);

  assemblyMenuPosition = {
    x: '0px',
    y: '0px'
  };

  columnsToDisplay = [
    'checkbox',
    'id',
    'image',
    'description',
    'brand',
    'article',
    'analogies',
    'stock',
    'cell',
    'retailControl',
    'quanControl',
    'remove'
  ];

  constructor(
    private store: Store<AppState>,
    public supportService: SupportService,
    private fb: FormBuilder,
    private title: Title,
    private computerService: ComputerService,
    private thermalPrintTargetService: ThermalPrintTargetService,
    private notificationService: NotificationService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {
    this.storeSubscription = this.store.subscribe((data) => {
      this.user = data.user.info;
      this.loadCart(data.cart);
    });
  }

  get cartSum() {
    return this.cart.controls.reduce((a, b) => {
      return a + Number(b.value.retail) * Number(b.value.quan);
    }, 0);
  }

  get cartItemsCount() {
    return this.cart.length;
  }

  get selectedCount() {
    return this.selectedProductsFormArray.length;
  }

  get selectionCountLabel() {
    return this.selectedCount > 0 ? `(${this.selectedCount})` : '';
  }

  get passProducts() {
    const cartRows = this.cart.value as ProductWithControls[];
    const selectedProductIds = new Set(
      this.selectedProductsFormArray.controls
        .map((control) => Number(control.value?.product?.id))
        .filter((id) => Number.isInteger(id) && id > 0)
    );

    if (selectedProductIds.size === 0) {
      return cartRows;
    }

    const selectedRows = cartRows.filter((row) => selectedProductIds.has(Number(row.product?.id)));
    return selectedRows.length > 0 ? selectedRows : cartRows;
  }

  loadCart(payload: ProductWithControls[]) {
    const newProducts = payload.filter((row) => {
      return !this.inProductArray(row.product);
    });
    const removedProducts: ProductWithControls[] = this.cart.value.filter(
      (row: ProductWithControls) => {
        return !this.inProductArray(row.product, payload);
      }
    );
    const productsInCart = payload.filter((row) => {
      return this.inProductArray(row.product);
    });
    newProducts.forEach((row) => {
      this.addToCartControl(row);
    });
    removedProducts.forEach((row) => {
      this.removeFromCartControl(row);
    });
    productsInCart.forEach((row) => {
      this.updateCartControl(row);
    });
    this.syncSelectedProducts();
    this.title.setTitle(`Корзина ${this.supportService.formatMoney(this.cartSum)}`);
  }

  inProductArray(product: Product, array: ProductWithControls[] = this.cart.value): boolean {
    const findIndex = array.findIndex((row: ProductWithControls) => {
      return row.product.id === product.id;
    });
    if (findIndex !== -1) {
      return true;
    }
    return false;
  }

  removeFromCartControl(payload: ProductWithControls) {
    const findProductIndex = this.cart.value.findIndex(
      (row: any) => row.product.id === payload.product.id
    );
    if (findProductIndex !== -1) {
      this.cart.removeAt(findProductIndex);
    }
    return;
  }

  addToCartControl(payload: ProductWithControls) {
    this.cart.push(
      this.fb.group({
        product: new FormControl(payload.product),
        quan: new FormControl(Number(payload.quan)),
        retail: new FormControl(Number(payload.retail))
      })
    );
    return;
  }

  updateCartControl(payload: ProductWithControls) {
    const findProductIndex = this.cart.value.findIndex(
      (row: any) => row.product.id === payload.product.id
    );
    if (findProductIndex !== -1) {
      if (payload.quan === 0) {
        return this.removeFromCartControl(payload);
      }
      const rowGroup: FormGroup<{ product: FormControl; quan: FormControl; retail: FormControl }> =
        this.cart.controls[findProductIndex] as FormGroup;
      if (rowGroup.value.quan !== payload.quan) {
        rowGroup.controls.quan.setValue(Number(payload.quan));
      }
      if (rowGroup.value.retail !== payload.retail) {
        rowGroup.controls.retail.setValue(Number(payload.retail));
      }
    }
  }

  rowChangeEvent(row: ProductWithControls) {
    if (Number(row.quan) === 0)
      return this.store.dispatch(removeFromCart({ payload: row.product }));
    this.store.dispatch(
      updateProductInCart({
        payload: row.product,
        quan: Number(row.quan),
        retail: Number(row.retail)
      })
    );
  }

  handleRemoveFromCart(product: Product) {
    return this.store.dispatch(removeFromCart({ payload: product }));
  }

  get products() {
    return this.cart.value.map((row: any) => row.product);
  }

  ngOnDestroy(): void {
    this.storeSubscription.unsubscribe();
  }

  clear() {
    this.store.dispatch(clearCart());
  }

  private syncSelectedProducts() {
    const cartProductIds = new Set(
      (this.cart.value as ProductWithControls[])
        .map((row) => Number(row.product?.id))
        .filter((id) => Number.isInteger(id) && id > 0)
    );

    for (let index = this.selectedProductsFormArray.length - 1; index >= 0; index--) {
      const selectedProductId = Number(this.selectedProductsFormArray.at(index)?.value?.product?.id);
      if (!cartProductIds.has(selectedProductId)) {
        this.selectedProductsFormArray.removeAt(index);
      }
    }
  }

  printAssemblySheet() {
    if (!this.user) return;
    const data = {
      warehouse: this.user.defaultWarehouse.description,
      warehouseId: this.user.defaultWarehouse.id,
      products: this.passProducts.map((row: any) => {
        return {
          id: row.product.id,
          description: row.product.description,
          brand: row.product.brand.description,
          article: row.product.article,
          quan: row.quan,
          cell: row.product.stock.find(
            (wh: any) => wh.warehouse.id === this.user?.defaultWarehouse.id
          )?.cell,
          nal: row.product.stock.find(
            (wh: any) => wh.warehouse.id === this.user?.defaultWarehouse.id
          )?.quan
        };
      })
    };
    this.computerService.printAssemblySheet(data);
  }

  openAssemblySheetMenu(event: MouseEvent) {
    event.preventDefault();

    this.assemblyMenuPosition = {
      x: `${event.clientX}px`,
      y: `${event.clientY}px`
    };
    this.cdr.detectChanges();
    this.assemblyMenuTrigger?.openMenu();
  }

  closeAssemblySheetMenu() {
    this.cdr.markForCheck();
  }

  async handleAssemblySheetMenuAction(mode: 'local' | 'agent', event: Event) {
    event.preventDefault();
    this.assemblyMenuTrigger?.closeMenu();

    if (mode === 'agent') {
      await this.printAssemblySheetToAnotherAgent();
      return;
    }

    this.printAssemblySheet();
  }

  async printAssemblySheetToAnotherAgent() {
    if (!this.user) return;

    const agentName = await this.thermalPrintTargetService.chooseAgent(this.user);
    if (!agentName) {
      return;
    }

    const data = {
      warehouse: this.user.defaultWarehouse.description,
      warehouseId: this.user.defaultWarehouse.id,
      products: this.passProducts.map((row: any) => {
        return {
          id: row.product.id,
          description: row.product.description,
          brand: row.product.brand.description,
          article: row.product.article,
          quan: row.quan,
          cell: row.product.stock.find(
            (wh: any) => wh.warehouse.id === this.user?.defaultWarehouse.id
          )?.cell,
          nal: row.product.stock.find(
            (wh: any) => wh.warehouse.id === this.user?.defaultWarehouse.id
          )?.quan
        };
      }),
      agentName
    };

    await this.computerService.printAssemblySheet(data);
    this.notificationService.notice(
      `РЎР±РѕСЂРѕС‡РЅС‹Р№ Р»РёСЃС‚ РѕС‚РїСЂР°РІР»РµРЅ РІ Р°РіРµРЅС‚ ${agentName}`
    );
  }
}
